"use client";

import * as React from "react";
import Link from "next/link";
import { Code, FileCode, Loader2, Rocket, Share2 } from "lucide-react";
import { Button } from "~/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "~/components/ui/accordion";
import { cn } from "~/lib/utils";
import { appDashboardUrl, buildProjectDashboardProfileUrl } from "~/lib/auth-paths";
import { buildWjsEmbedScriptUrl } from "~/lib/widget-script";
import { formatHirePrice } from "~/lib/marketplace/hire-pricing";
import type { PricingLineItem } from "~/lib/marketplace/hire-recommendations-pricing";
import { toast } from "sonner";

type PanelTab = "test" | "deploy";

type HireCustomDeployPanelProps = {
  previewProjectId: string | null;
  /** When false, show generate CTA even if a draft project id exists. */
  previewReady?: boolean;
  deployedProjectId: string | null;
  agentName: string;
  totalLabel: string;
  pricingLineItems: PricingLineItem[];
  isFree: boolean;
  isSubmitting: boolean;
  isGenerating: boolean;
  previewVersion?: number;
  previewRefreshKey?: number;
  preferredTab?: PanelTab | null;
  onGenerate?: () => void;
  onDeploy: () => void;
  /** Wizard mode: preview tab only (hire on summary step). */
  testOnly?: boolean;
  className?: string;
};

function HireDeployPricingSummary({
  lineItems,
  totalLabel,
  isFree,
}: {
  lineItems: PricingLineItem[];
  totalLabel: string;
  isFree: boolean;
}) {
  return (
    <div className="w-full max-w-sm rounded-lg border border-slate-200 bg-slate-50 p-4 text-left">
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
        Monthly subscription
      </p>
      {isFree ? (
        <p className="mt-2 text-sm text-slate-600">
          Live Chat and basic setup are included at no monthly cost.
        </p>
      ) : (
        <ul className="mt-3 space-y-2">
          {lineItems.map((item) => (
            <li key={item.label} className="flex items-center justify-between gap-3 text-sm">
              <span className="text-slate-700">{item.label}</span>
              <span className="shrink-0 font-medium text-slate-900">
                {formatHirePrice(item.amountCents)}/mo
              </span>
            </li>
          ))}
        </ul>
      )}
      <div className="mt-4 flex items-center justify-between border-t border-slate-200 pt-3">
        <span className="text-sm font-semibold text-slate-900">Total</span>
        <span className="text-base font-bold text-slate-900">{totalLabel}</span>
      </div>
      {!isFree ? (
        <p className="mt-2 text-xs text-slate-500">
          Pricing reflects your current settings. You&apos;ll confirm this total on secure checkout
          before payment.
        </p>
      ) : null}
    </div>
  );
}

function HireDeployEmbedSection({ projectId }: { projectId: string }) {
  const appOrigin =
    typeof window !== "undefined"
      ? window.location.origin
      : (process.env.NEXT_PUBLIC_APP_URL ?? "https://app.easygpt.builders");

  const embeddableScript = `<script src="${buildWjsEmbedScriptUrl(appOrigin, projectId)}"></script>`;
  const iframeUrl = `<iframe src="${appOrigin}/w/${projectId}" width="100%" height="100%" title="EasyGPT Chat" />`;
  const gtmCode = `<!-- Google Tag Manager -->
<script>(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','GTM-SAMPLE-ID');</script>
<!-- End Google Tag Manager -->

<!-- Google Tag Manager (noscript) -->
<noscript><iframe src="https://www.googletagmanager.com/ns.html?id=GTM-SAMPLE-ID"
height="0" width="0" style="display:none;visibility:hidden"></iframe></noscript>
<!-- End Google Tag Manager (noscript) -->`;

  const copyToClipboard = async (code: string) => {
    try {
      await navigator.clipboard.writeText(code);
      toast.success("Code copied to clipboard");
    } catch {
      toast.error("Failed to copy code");
    }
  };

  const renderCodeBlock = (code: string) => (
    <AccordionContent className="space-y-3">
      <div className="rounded-lg bg-slate-50 p-3">
        <code className="block overflow-x-auto whitespace-pre-wrap text-xs text-slate-700">{code}</code>
      </div>
      <Button type="button" variant="outline" size="sm" onClick={() => void copyToClipboard(code)}>
        Copy to Clipboard
      </Button>
    </AccordionContent>
  );

  return (
    <div className="flex min-h-0 flex-col">
      <div className="shrink-0">
        <h4 className="text-base font-semibold text-slate-900">Add Inbound GPT Agent to your website</h4>
        <p className="mt-1 text-sm text-slate-600">
          Choose from multiple integration options to add AI-powered functionality to your website.
        </p>
      </div>

      <div className="mt-4 min-h-0 overflow-y-auto pr-1">
        <Accordion type="multiple" defaultValue={["script", "gtm", "iframe"]}>
        <AccordionItem value="script">
          <AccordionTrigger className="text-sm font-medium hover:no-underline">
            <span className="flex items-center gap-2">
              <Code className="h-4 w-4 text-primary" />
              Website Embed Script
            </span>
          </AccordionTrigger>
          {renderCodeBlock(embeddableScript)}
        </AccordionItem>

        <AccordionItem value="gtm">
          <AccordionTrigger className="text-sm font-medium hover:no-underline">
            <span className="flex items-center gap-2">
              <Share2 className="h-4 w-4 text-primary" />
              Google Tag Manager Code
            </span>
          </AccordionTrigger>
          {renderCodeBlock(gtmCode)}
        </AccordionItem>

        <AccordionItem value="iframe">
          <AccordionTrigger className="text-sm font-medium hover:no-underline">
            <span className="flex items-center gap-2">
              <FileCode className="h-4 w-4 text-primary" />
              Website iFrame Embed
            </span>
          </AccordionTrigger>
          {renderCodeBlock(iframeUrl)}
        </AccordionItem>
      </Accordion>
      </div>
    </div>
  );
}

export function HireCustomDeployPanel({
  previewProjectId,
  previewReady = false,
  deployedProjectId,
  agentName,
  totalLabel,
  pricingLineItems,
  isFree,
  isSubmitting,
  isGenerating,
  previewVersion = 0,
  previewRefreshKey = 0,
  preferredTab,
  onGenerate,
  onDeploy,
  testOnly = false,
  className,
}: HireCustomDeployPanelProps) {
  const [activeTab, setActiveTab] = React.useState<PanelTab>("test");
  const [iframeKey, setIframeKey] = React.useState(0);
  const displayAgentName = agentName.trim() || "your agent";

  React.useEffect(() => {
    if (previewVersion > 0) {
      setIframeKey((k) => k + 1);
      setActiveTab("test");
    }
  }, [previewVersion]);

  React.useEffect(() => {
    if (previewRefreshKey > 0) {
      setIframeKey((k) => k + 1);
    }
  }, [previewRefreshKey]);

  React.useEffect(() => {
    if (deployedProjectId) {
      setActiveTab("deploy");
    }
  }, [deployedProjectId]);

  React.useEffect(() => {
    if (preferredTab) {
      setActiveTab(preferredTab);
    }
  }, [preferredTab]);

  const iframeSrc =
    previewProjectId && typeof window !== "undefined"
      ? `${window.location.origin}/agent/${previewProjectId}/Agent_Profile?embed=builder&refresh=${iframeKey}`
      : previewProjectId
        ? `/agent/${previewProjectId}/Agent_Profile?embed=builder&refresh=${iframeKey}`
        : null;

  const showPreviewIframe = Boolean(previewProjectId && previewReady && iframeSrc);

  const tabClass = (tab: PanelTab) =>
    cn(
      "rounded-lg px-4 py-2.5 text-sm font-semibold transition sm:text-base",
      activeTab === tab
        ? "bg-slate-900 text-white shadow-sm"
        : "bg-slate-100 text-slate-600 hover:bg-slate-200 hover:text-slate-900",
    );

  return (
    <div className={cn("flex min-h-0 w-full min-w-0 flex-1 flex-col bg-white", className)}>
      <div className="relative flex shrink-0 items-center justify-center border-b border-slate-100 px-4 py-3.5">
        <p className="absolute left-4 top-1/2 -translate-y-1/2 text-xs font-medium uppercase tracking-wide text-slate-400">
          Preview
        </p>
        <div
          className="flex max-w-full flex-wrap items-center justify-center gap-2 px-2"
          role="tablist"
          aria-label="Agent preview mode"
        >
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "test"}
            className={tabClass("test")}
            onClick={() => setActiveTab("test")}
          >
            Test Inbound GPT Agent
          </button>
          {!testOnly ? (
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "deploy"}
            className={tabClass("deploy")}
            onClick={() => setActiveTab("deploy")}
          >
            {`Onboard ${displayAgentName}`}
          </button>
          ) : null}
        </div>
      </div>

      <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden">
        {activeTab === "test" ? (
          showPreviewIframe ? (
            <div className="relative min-h-0 flex-1 overflow-hidden bg-white">
              <iframe
                key={iframeKey}
                title="Agent profile preview"
                src={iframeSrc!}
                scrolling="yes"
                allow="microphone; camera; fullscreen; clipboard-write"
                className="absolute inset-0 h-full w-full border-0 bg-white"
              />
            </div>
          ) : (
            <div className="flex min-h-[520px] flex-1 flex-col items-center justify-center gap-6 p-8 text-center">
              <div className="max-w-sm space-y-2">
                <p className="text-sm font-medium text-slate-900">
                  {isGenerating
                    ? "Generating your Inbound GPT Agent preview…"
                    : "Review your recommended Inbound GPT profile and company settings."}
                </p>
                {!isGenerating ? (
                  <p className="text-sm text-slate-600">
                    Your live agent profile preview will appear here automatically.
                  </p>
                ) : null}
              </div>
              {isGenerating ? (
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              ) : onGenerate ? (
                <Button
                  type="button"
                  size="lg"
                  className="min-w-[240px]"
                  onClick={onGenerate}
                  disabled={isSubmitting || isGenerating}
                >
                  {`Generate Inbound GPT Agent (${displayAgentName})`}
                </Button>
              ) : (
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              )}
            </div>
          )
        ) : deployedProjectId ? (
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden p-6">
            <div className="mb-6 shrink-0 space-y-4 text-center">
              <h3 className="text-xl font-bold text-slate-900">Inbound GPT Agent Onboarding Complete</h3>
              <Button asChild size="lg" className="min-w-[200px]">
                <Link
                  href={
                    deployedProjectId
                      ? buildProjectDashboardProfileUrl(deployedProjectId)
                      : appDashboardUrl()
                  }
                >
                  Open My Profile
                </Link>
              </Button>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto rounded-lg border border-slate-200 bg-slate-50/50 p-4">
              <HireDeployEmbedSection projectId={deployedProjectId} />
            </div>
          </div>
        ) : (
          <div className="flex min-h-[520px] flex-1 flex-col items-center justify-center gap-6 overflow-y-auto p-8 text-center">
            <div className="max-w-sm space-y-2">
              <p className="text-sm font-medium text-slate-900">Ready to go live?</p>
              <p className="text-sm text-slate-600">
                Review your monthly pricing below, then deploy to get website embed codes.
              </p>
            </div>
            <HireDeployPricingSummary
              lineItems={pricingLineItems}
              totalLabel={totalLabel}
              isFree={isFree}
            />
            <Button
              type="button"
              size="lg"
              className="min-w-[200px]"
              onClick={onDeploy}
              disabled={isSubmitting || isGenerating}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deploying…
                </>
              ) : (
                <>
                  <Rocket className="mr-2 h-4 w-4" />
                  {isFree
                    ? `Onboard ${displayAgentName} for Free`
                    : `Onboard ${displayAgentName}`}
                </>
              )}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
